import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import {
  useKorintConfig,
  getStoredSession,
  performAnonymousSessionHandoff,
} from 'korint-sdk/react';
import { AuthLayout } from '../auth-layout';
import { PasswordSetupForm } from '../password-setup-form';
import { useAuthRedirect } from '../useAuthRedirect';
import { Text } from '@folio/components/ui/text';

export const PasswordSetupView = ({ logo }: { logo: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clientOptions } = useKorintConfig();
  // Only used by single-tenant apps, where tenant is always set.
  const tenantId = clientOptions.tenant ?? '';
  const { redirectUrl, redirectSearch } = useAuthRedirect();

  const email = searchParams.get('email') ?? '';
  const code = searchParams.get('code');

  if (!email) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <AuthLayout
      title={t('authPasswordSetup.title')}
      logo={logo}
      footer={
        <Text size="sm" className="text-center text-gray-500">
          <Link to={{ pathname: '/auth', search: redirectSearch }} className="text-primary">
            {t('authSignIn.useDifferentEmail')}
          </Link>
        </Text>
      }
    >
      <PasswordSetupForm
        email={email}
        code={code}
        onSuccess={async () => {
          if (getStoredSession(tenantId)) {
            await performAnonymousSessionHandoff(tenantId);
          }
          navigate(redirectUrl);
        }}
      />
    </AuthLayout>
  );
};
